/**
 * Iter 235b — Page publique du quiz de positionnement.
 *
 * URL : /quiz/:token (PUBLIC, pas d'auth)
 * L'apprenant arrive depuis le welcome email J-7. Il répond aux questions
 * (QCM, QCM multi, échelle 1-5, texte) puis voit son score + level + un message
 * de remerciement. Le score est stocké en DB et le formateur le verra dans le CRM.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, CheckCircle2, AlertTriangle, ClipboardList, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicQuizPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    setState({ loading: true, data: null, error: null });
    try {
      const r = await axios.get(API + "/quiz/public/" + token);
      setState({ loading: false, data: r.data, error: null });
    } catch (e) {
      setState({ loading: false, data: null, error: e.response?.data?.detail || e.message });
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const questions = state.data?.questions || [];
    const required = questions.filter((q) => q.required !== false);
    const missing = required.filter((q) => answers[q.id] == null || answers[q.id] === "");
    if (missing.length > 0) {
      toast.error(`${missing.length} question(s) restante(s)`);
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(API + "/quiz/public/" + token + "/submit", { answers });
      setResult(r.data);
      toast.success("Questionnaire soumis !");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
    setSubmitting(false);
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center bg-[#14101c]"><Loader2 className="w-10 h-10 animate-spin text-purple-400" /></div>;

  if (state.error) return (
    <div className="min-h-screen flex items-center justify-center bg-[#14101c] px-4">
      <div className="bg-[#0b0811] border border-red-500/30 rounded-2xl p-8 max-w-md text-center">
        <AlertTriangle className="w-12 h-12 mx-auto text-red-400 mb-3" />
        <h1 className="text-xl font-black text-white mb-2">Lien invalide</h1>
        <p className="text-sm text-slate-400">{state.error}</p>
      </div>
    </div>
  );

  const d = state.data;

  if (d.status === "completed") return (
    <div className="min-h-screen flex items-center justify-center bg-[#14101c] px-4">
      <div className="bg-[#0b0811] border border-green-500/30 rounded-2xl p-8 max-w-md text-center" data-testid="quiz-already-completed">
        <CheckCircle2 className="w-14 h-14 mx-auto text-green-400 mb-3" />
        <h1 className="text-2xl font-black text-white mb-2">Déjà complété</h1>
        <p className="text-sm text-slate-400">{d.message}</p>
        {d.score?.global_score != null && (
          <div className="mt-4 inline-block bg-white/5 rounded-lg px-4 py-2">
            <div className="text-[10px] uppercase text-slate-500">Votre score</div>
            <div className="text-2xl font-bold text-purple-300">{d.score.global_score}/100</div>
          </div>
        )}
      </div>
    </div>
  );

  if (result) return <QuizResultScreen result={result} learnerName={d.learner_name} />;

  return (
    <div className="min-h-screen bg-[#14101c] py-10 px-4" data-testid="public-quiz-page">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto bg-purple-500/20 border border-purple-500/40 rounded-2xl flex items-center justify-center mb-4">
            <ClipboardList className="w-7 h-7 text-purple-300" />
          </div>
          <h1 className="text-3xl font-black text-white">{d.quiz_name}</h1>
          {d.description && <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">{d.description}</p>}
          {d.learner_name && <p className="text-purple-300 text-sm mt-3">Bonjour <strong>{d.learner_name}</strong></p>}
        </div>

        <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4 mb-6 text-xs text-slate-300">
          <strong className="text-purple-300">🎯 Pourquoi ce questionnaire ?</strong> Il permet à votre formateur d'<em>adapter la formation à votre niveau</em> (Qualiopi, indicateur 12). Vos réponses ne sont pas notées : il n'y a pas de bonne ou mauvaise réponse, soyez honnête !
        </div>

        <div className="space-y-4">
          {(d.questions || []).map((q, i) => (
            <QuestionCard key={q.id || i} question={q} index={i + 1} value={answers[q.id]} onChange={(v) => setAnswers({ ...answers, [q.id]: v })} />
          ))}
        </div>

        <Button onClick={submit} disabled={submitting} className="w-full mt-8 bg-purple-500 hover:bg-purple-400 text-white font-bold h-12" data-testid="quiz-submit-btn">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <ArrowRight className="w-5 h-5 mr-2" />}
          Soumettre mes réponses
        </Button>

        <p className="text-[10px] text-center text-slate-600 mt-4">Blitzz · questionnaire de positionnement Qualiopi · indicateur 12</p>
      </div>
    </div>
  );
}


// ─── Question Card ────────────────────────────────────


function QuestionCard({ question, index, value, onChange }) {
  const q = question;
  return (
    <div className="bg-[#0b0811] border border-white/10 rounded-xl p-5" data-testid={`quiz-question-${q.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-300 flex-shrink-0">{index}</div>
        <div className="flex-1">
          <div className="text-sm font-medium text-white">{q.question || q.label}</div>
          {q.help_text && <div className="text-[11px] text-slate-500 mt-1">{q.help_text}</div>}
        </div>
      </div>

      <div className="ml-10">
        {q.type === "qcm" && (
          <div className="space-y-2">
            {(q.options || []).map((opt, i) => (
              <label key={i} className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer border transition-colors ${value === i ? "bg-purple-500/15 border-purple-500/40 text-white" : "bg-white/5 border-white/5 text-slate-300 hover:bg-white/10"}`}>
                <input type="radio" name={q.id} checked={value === i} onChange={() => onChange(i)} className="text-purple-500" data-testid={`quiz-${q.id}-opt-${i}`} />
                <span className="text-sm">{opt}</span>
              </label>
            ))}
          </div>
        )}

        {q.type === "qcm_multi" && (
          <div className="space-y-2">
            {(q.options || []).map((opt, i) => {
              const arr = Array.isArray(value) ? value : [];
              const checked = arr.includes(i);
              return (
                <label key={i} className={`flex items-center gap-2 p-2.5 rounded-lg cursor-pointer border ${checked ? "bg-purple-500/15 border-purple-500/40 text-white" : "bg-white/5 border-white/5 text-slate-300 hover:bg-white/10"}`}>
                  <input type="checkbox" checked={checked} onChange={(e) => {
                    onChange(e.target.checked ? [...arr, i] : arr.filter((x) => x !== i));
                  }} />
                  <span className="text-sm">{opt}</span>
                </label>
              );
            })}
          </div>
        )}

        {q.type === "scale" && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500">{q.scale_label_min || "Faible"}</span>
            <div className="flex gap-1.5 flex-1 justify-center">
              {Array.from({ length: q.scale_max || 5 }).map((_, i) => {
                const v = i + 1;
                return (
                  <button key={i} onClick={() => onChange(v)}
                    className={`w-10 h-10 rounded-lg font-bold text-sm border transition-colors ${value === v ? "bg-purple-500 text-white border-purple-400" : "bg-white/5 border-white/10 text-slate-400 hover:bg-white/10"}`}
                    data-testid={`quiz-${q.id}-scale-${v}`}>
                    {v}
                  </button>
                );
              })}
            </div>
            <span className="text-xs text-slate-500">{q.scale_label_max || "Fort"}</span>
          </div>
        )}

        {(q.type === "text" || q.type === "long_text") && (
          <textarea
            rows={q.type === "long_text" ? 4 : 2}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            placeholder={q.placeholder || "Votre réponse..."}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white"
            data-testid={`quiz-${q.id}-text`}
          />
        )}
      </div>
    </div>
  );
}


// ─── Result Screen ────────────────────────────────────


function QuizResultScreen({ result, learnerName }) {
  const score = result.score || {};
  return (
    <div className="min-h-screen bg-[#14101c] py-10 px-4" data-testid="quiz-result">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto bg-emerald-500/20 border border-emerald-500/40 rounded-2xl flex items-center justify-center mb-4">
            <CheckCircle2 className="w-9 h-9 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-black text-white mb-1">Merci{learnerName ? `, ${learnerName.split(" ")[0]}` : ""} !</h1>
          <p className="text-slate-400 text-sm">{result.thank_you || "Vos réponses ont bien été enregistrées."}</p>
        </div>

        <div className="bg-gradient-to-br from-purple-500/15 to-fuchsia-500/15 border border-purple-500/30 rounded-2xl p-6 mb-4">
          <div className="text-[10px] uppercase tracking-wider text-purple-400 font-bold mb-2">Votre score global</div>
          <div className="flex items-baseline gap-2">
            <div className="text-5xl font-black text-white">{score.global_score || 0}</div>
            <div className="text-slate-400">/100</div>
          </div>
          <div className="text-sm text-purple-300 mt-2">Niveau : <strong>{score.global_level || "—"}</strong></div>
        </div>

        {Object.keys(score.by_competency || {}).length > 0 && (
          <div className="bg-[#0b0811] border border-white/10 rounded-2xl p-5 mb-4">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-3">Par compétence</div>
            <div className="space-y-3">
              {Object.entries(score.by_competency).map(([cid, c]) => (
                <div key={cid}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-200">{c.name}</span>
                    <span className="text-purple-300 font-mono">{c.score_pct}% · {c.level}</span>
                  </div>
                  <div className="bg-white/5 rounded-full h-2">
                    <div className="bg-gradient-to-r from-purple-500 to-fuchsia-400 h-2 rounded-full transition-all" style={{ width: `${c.score_pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {result.ai_recommendations?.summary && (
          <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-cyan-300" />
              <span className="text-[10px] uppercase tracking-wider text-cyan-400 font-bold">Pour votre formateur</span>
            </div>
            <p className="text-sm text-slate-200 leading-relaxed">{result.ai_recommendations.summary}</p>
          </div>
        )}

        <p className="text-[10px] text-center text-slate-600 mt-6">
          Vos réponses sont confidentielles et ne seront utilisées que pour adapter la formation. À très vite !
        </p>
      </div>
    </div>
  );
}
